/**
 *
 * @description Operations on numbers
 */

/**
 *
 * @description Get the range of numbers from start to end (exclusive) with an optional step
 * @param start The start of the range
 * @param end The end of the range
 * @param step The step between numbers
 * @returns An array of numbers from start to end (exclusive) with an optional step
 * @example
 * const range = range(0, 5, 2);
 * // range = [0, 2, 4]
 * const range2 = range(5);
 * // range2 = [0, 1, 2, 3, 4]
 * const range3 = range(5, 0, -1);
 * // range3 = [5, 4, 3, 2, 1]
 * const range4 = range(5, 0, -2);
 * // range4 = [5, 3, 1]
 */
export const range = (start: number, end?: number, step?: number): number[] => {
  const _start = end === undefined ? 0 : start;
  const _end = end === undefined ? start : end;
  const _step = step === undefined ? 1 : step;
  const result = [];
  for (let i = _start; i < _end; i += _step) {
    result.push(i);
  }
  return result;
};

function findOrderOfMagnitude(number: number): number {
  return Math.floor(Math.log10(Math.abs(number)));
}

function calculateFactor(orderOfMagnitude: number): number {
  return Math.pow(10, orderOfMagnitude);
}

function roundToSignificantDigits(number: number, factor: number): number {
  return Math.round(number / factor) * factor;
}

/**
 *
 * @description Round number adaptatively accord to the most significant digits
 * @example
 * console.log(adaptiveRound(1004.0005)); // 1004
 * console.log(adaptivaRound(0.00008005)); // 0.00008
 */
export function adaptiveRound(number: number): number {
  if (number === 0) {
    return 0;
  }

  const orderOfMagnitude = findOrderOfMagnitude(number);
  const factor = calculateFactor(orderOfMagnitude);
  const roundedNumber = roundToSignificantDigits(number, factor);

  return roundedNumber;
}

export function ensureNumber(number: number | string): number | undefined {
  if (typeof number === 'number') return number;
  if (typeof number === 'string' && !isNaN(Number.parseFloat(number))) {
    return Number.parseFloat(number);
  }
  return undefined;
}
